<template>
  <div class="user-menu-mobile">
    <button
      type="button"
      class="user-menu-mobile__avatar-btn"
      :aria-label="$t('account')"
      @click="openSheet"
    >
      <span class="user-menu-mobile__avatar-chip">
        <img
          v-if="hasRealAvatar"
          class="user-menu-mobile__avatar-chip-img"
          :src="displayAvatar"
          alt=""
        />
        <span
          v-else-if="hasUsableInitials"
          class="user-menu-mobile__avatar-chip-initials"
        >
          {{ userInitials }}
        </span>
        <img
          v-else
          class="user-menu-mobile__avatar-chip-img"
          :src="avatarPlaceholder"
          alt=""
        />
      </span>
    </button>

    <!-- Level 1: sheet with report + 3 menus -->
    <v-bottom-sheet v-model="menuOpen" scrollable>
      <v-card class="user-menu-mobile__sheet">
        <div
          class="user-menu-mobile__handle"
          role="button"
          :aria-label="$t('close')"
          @touchstart.passive="onSheetHandleTouchStart"
          @touchend="onSheetHandleTouchEnd"
          @click="closeMenu"
        />

        <div class="user-menu-mobile__header" dir="rtl">
          <span class="user-menu-mobile__header-spacer" />
          <div class="user-menu-mobile__header-title">{{ $t("account") }}</div>
          <button
            type="button"
            class="user-menu-mobile__icon-btn"
            :aria-label="$t('back')"
            @click="closeMenu"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>

        <div class="user-menu-mobile__body" dir="rtl">
          <!-- user identity + account summary -->
          <div class="user-menu-mobile__user">
            <div class="user-menu-mobile__user-photo-wrap">
              <img
                class="user-menu-mobile__user-avatar"
                :src="displayAvatar"
                alt=""
              />
              <button
                type="button"
                class="user-menu-mobile__user-edit"
                :aria-label="$t('account-setting')"
                @click="openProfileDialog"
              >
                <v-icon size="16" color="#fff">mdi-pencil</v-icon>
              </button>
            </div>
            <div class="user-menu-mobile__user-texts">
              <div class="user-menu-mobile__user-name">
                {{ displayUserName }}
              </div>
              <div class="user-menu-mobile__user-role">
                {{ displayUserRole }}
              </div>
            </div>

            <div class="user-menu-mobile__user-summary">
              <div v-if="displayMobile" class="user-menu-mobile__user-summary-row">
                <span>{{ $t("mobile") }}</span>
                <strong>{{ displayMobile }}</strong>
              </div>
              <div v-if="displayEmail" class="user-menu-mobile__user-summary-row">
                <span>{{ $t("email") }}</span>
                <strong>{{ displayEmail }}</strong>
              </div>
              <div v-if="displayUsername" class="user-menu-mobile__user-summary-row">
                <span>{{ $t("username") }}</span>
                <strong>{{ displayUsername }}</strong>
              </div>
            </div>

            <button
              type="button"
              class="user-menu-mobile__logout-btn"
              :disabled="loadingLogOut"
              @click="logout"
            >
              <v-progress-circular
                v-if="loadingLogOut"
                indeterminate
                size="16"
                width="2"
                color="#E53935"
              />
              <template v-else>
                <v-icon size="18" color="#E53935" class="ml-1">mdi-logout</v-icon>
                {{ $t("exit-account") }}
              </template>
            </button>
          </div>

          <!-- title report (not a menu) -->
          <div class="user-menu-mobile__report">
            <img
              class="user-menu-mobile__report-logo"
              :src="businessLogo"
              alt=""
            />
            <div class="user-menu-mobile__report-texts">
              <div class="user-menu-mobile__report-name">
                {{ userInfo?.init?.abrName || "-" }}
              </div>
              <div class="user-menu-mobile__report-key">
                #{{ userInfo?.init?.abrKey || "-" }}
              </div>
              <div class="user-menu-mobile__report-line">
                <template v-if="userCurrentPlan">
                  {{ userCurrentPlan.plan?.name || "-" }}
                  <span class="user-menu-mobile__dot">/</span>
                  {{ $t("subscription end date") }}
                  {{ userCurrentPlan.ends_at || "-" }}
                </template>
                <template v-else>-</template>
              </div>
              <div class="user-menu-mobile__report-line">
                {{ $t("sms-count") }}
                {{ smsCountRemain > 0 ? smsCountRemain : 0 }}
              </div>
            </div>
            <button
              type="button"
              class="user-menu-mobile__row-action user-menu-mobile__report-action"
              :aria-label="$t('change-account')"
              :disabled="loadingAccounts"
              @click="openChangeAccount"
            >
              <v-progress-circular
                v-if="loadingAccounts"
                indeterminate
                size="16"
                width="2"
                color="#5B7C99"
              />
              <v-icon v-else size="18" color="#5B7C99">mdi-swap-horizontal</v-icon>
            </button>
          </div>

          <!-- menus -->
          <button
            type="button"
            class="user-menu-mobile__row"
            @click="openPage('business')"
          >
            <span class="user-menu-mobile__row-icon">
              <my-icon
                name="shopping-cart"
                :width="18"
                :height="18"
                fill="#5B7C99"
                :cursor-pointer="true"
              />
            </span>
            <span class="user-menu-mobile__row-label">
              {{ $t("planAdministration") }}
            </span>
            <v-icon size="18" color="#90A4AE">mdi-chevron-left</v-icon>
          </button>

          <button
            type="button"
            class="user-menu-mobile__row"
            @click="openPage('settings')"
          >
            <span class="user-menu-mobile__row-icon">
              <my-icon
                name="setting-icon"
                :width="18"
                :height="18"
                fill="#5B7C99"
                :cursor-pointer="true"
              />
            </span>
            <span class="user-menu-mobile__row-label">
              {{ $t("template-setting") }} و {{ $t("updated") }}
            </span>
            <v-icon size="18" color="#90A4AE">mdi-chevron-left</v-icon>
          </button>
        </div>
      </v-card>
    </v-bottom-sheet>

    <!-- Level 2: business & subscription full page -->
    <v-dialog
      v-model="pageBusiness"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
    >
      <v-card
        class="user-menu-mobile__page"
        :class="{ 'user-menu-mobile__page--accounts': showAccountsList }"
      >
        <div class="user-menu-mobile__page-header" dir="rtl">
          <span class="user-menu-mobile__header-spacer" />
          <div class="user-menu-mobile__header-title">
            {{
              showAccountsList
                ? $t("change-account")
                : $t("planAdministration")
            }}
          </div>
          <button
            type="button"
            class="user-menu-mobile__icon-btn"
            :aria-label="$t('back')"
            @click="closePage"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>

        <div class="user-menu-mobile__page-body" dir="rtl">
          <template v-if="!showAccountsList">
            <div class="user-menu-mobile__report user-menu-mobile__report--page">
              <img
                class="user-menu-mobile__report-logo"
                :src="businessLogo"
                alt=""
              />
              <div class="user-menu-mobile__report-texts">
                <div class="user-menu-mobile__report-name">
                  {{ userInfo?.init?.abrName || "-" }}
                </div>
                <div class="user-menu-mobile__report-key">
                  #{{ userInfo?.init?.abrKey || "-" }}
                </div>
                <div v-if="userCurrentPlan" class="user-menu-mobile__report-line">
                  {{ userCurrentPlan.plan?.name }}
                  ·
                  {{ $t("subscription end date") }}
                  {{ userCurrentPlan.ends_at }}
                </div>
                <div class="user-menu-mobile__report-line">
                  {{ $t("sms-count") }}
                  {{ smsCountRemain > 0 ? smsCountRemain : 0 }}
                </div>
              </div>
            </div>

            <button type="button" class="user-menu-mobile__row" @click="goToPlans">
              <span class="user-menu-mobile__row-icon">
                <my-icon
                  name="shopping-cart"
                  :width="18"
                  :height="18"
                  fill="#5B7C99"
                  :cursor-pointer="true"
                />
              </span>
              <span class="user-menu-mobile__row-label">{{
                $t("Purchase and renewal of subscription")
              }}</span>
              <v-icon size="18" color="#90A4AE">mdi-chevron-left</v-icon>
            </button>

            <button
              type="button"
              class="user-menu-mobile__row"
              @click="chargingPanelSMS"
            >
              <span class="user-menu-mobile__row-icon">
                <my-icon
                  name="chat-comment"
                  :width="18"
                  :height="18"
                  stroke="#5B7C99"
                  :cursor-pointer="true"
                />
              </span>
              <span class="user-menu-mobile__row-label">{{
                $t("increase SMS validity")
              }}</span>
              <v-icon size="18" color="#90A4AE">mdi-chevron-left</v-icon>
            </button>
          </template>

          <template v-else>
            <button
              v-for="item in userAccounts"
              :key="item.id"
              type="button"
              class="user-menu-mobile__row user-menu-mobile__account-row"
              :disabled="loadingAccounts"
              @click="findBusiness(item)"
            >
              <img
                class="user-menu-mobile__report-logo"
                :src="item.logo?.link || require('@/assets/logo-abr-iron.png')"
                alt=""
              />
              <span class="user-menu-mobile__row-texts">
                <span class="user-menu-mobile__row-label">{{
                  item.name ? item.name : "-"
                }}</span>
                <span class="user-menu-mobile__row-sub">{{
                  item.app_key ? "#" + item.app_key : "-"
                }}</span>
              </span>
              <v-chip
                v-if="item.plan_days_left == 0 || item.plan_days_left < 0"
                color="red"
                small
                text-color="white"
              >
                {{ $t("expired") }}
              </v-chip>
              <span v-else class="user-menu-mobile__days">
                {{ item.plan_days_left ? item.plan_days_left : "-" }}
                {{ $t("days-left") }}
              </span>
            </button>
          </template>
        </div>

        <div
          v-if="showAccountsList"
          class="user-menu-mobile__page-footer"
        >
          <v-btn
            outlined
            color="primary"
            block
            height="48"
            class="user-menu-mobile__create-business-btn"
            @click="goCreateBusiness"
          >
            {{ $t("create-new-business") }}
            <v-icon size="20" class="mr-2">mdi-plus</v-icon>
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Level 2: template & updates full page -->
    <v-dialog
      v-model="pageSettings"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
    >
      <v-card class="user-menu-mobile__page">
        <div class="user-menu-mobile__page-header" dir="rtl">
          <span class="user-menu-mobile__header-spacer" />
          <div class="user-menu-mobile__header-title">
            {{ $t("template-setting") }} و {{ $t("updated") }}
          </div>
          <button
            type="button"
            class="user-menu-mobile__icon-btn"
            :aria-label="$t('back')"
            @click="closePage"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>

        <div class="user-menu-mobile__page-body" dir="rtl">
          <button
            type="button"
            class="user-menu-mobile__row"
            @click="openTemplateSetting"
          >
            <span class="user-menu-mobile__row-icon">
              <my-icon
                name="setting-icon"
                :width="18"
                :height="18"
                fill="#5B7C99"
                :cursor-pointer="true"
              />
            </span>
            <span class="user-menu-mobile__row-label">{{ $t("template-setting") }}</span>
            <v-icon size="18" color="#90A4AE">mdi-chevron-left</v-icon>
          </button>

          <button type="button" class="user-menu-mobile__row" @click="openUpdates">
            <span class="user-menu-mobile__row-icon">
              <my-icon
                name="update-icon"
                :width="18"
                :height="18"
                stroke="#5B7C99"
                :cursor-pointer="true"
              />
            </span>
            <span class="user-menu-mobile__row-label">{{ $t("updated") }}</span>
            <v-icon size="18" color="#90A4AE">mdi-chevron-left</v-icon>
          </button>
        </div>
      </v-card>
    </v-dialog>

    <v-dialog v-model="profileDialog" fullscreen hide-overlay width="600">
      <v-card class="user-menu-mobile__page">
        <div class="user-menu-mobile__page-header" dir="rtl">
          <span class="user-menu-mobile__header-spacer" />
          <div class="user-menu-mobile__header-title">
            {{ $t("account-setting") }}
          </div>
          <button
            type="button"
            class="user-menu-mobile__icon-btn"
            :aria-label="$t('back')"
            @click="profileDialog = false"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>
        <div class="user-menu-mobile__page-body">
          <account-profile ref="profileRef" page />
        </div>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="templateSettingDialog"
      fullscreen
      hide-overlay
      width="400"
    >
      <v-card class="user-menu-mobile__page">
        <div class="user-menu-mobile__page-header" dir="rtl">
          <span class="user-menu-mobile__header-spacer" />
          <div class="user-menu-mobile__header-title">
            {{ $t("template-setting") }}
          </div>
          <button
            type="button"
            class="user-menu-mobile__icon-btn"
            :aria-label="$t('back')"
            @click="templateSettingDialog = false"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>
        <div class="user-menu-mobile__page-body">
          <setting />
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import AccountProfile from "./AccountProfile.vue";
// Redesigned theme settings (core/Settings.vue kept untouched)
import Setting from "@/views/dashboard/components/core/appbar/partial/theme/ThemeSettings.vue";
import MyIcon from "@/views/dashboard/components/general/icon/MyIcon.vue";
import { mapState } from "vuex";

export default {
  name: "UserMenuMobile",
  components: {
    AccountProfile,
    Setting,
    MyIcon,
  },
  data() {
    return {
      menuOpen: false,
      pageBusiness: false,
      pageSettings: false,
      showAccountsList: false,
      userInfo: null,
      serviceInfo: null,
      profileDialog: false,
      templateSettingDialog: false,
      loadingLogOut: false,
      userCurrentPlan: null,
      tokenUserApp: null,
      userAccounts: null,
      loadingAccounts: false,
      sheetHandleTouchY: null,
      profileUser: null,
    };
  },
  computed: {
    ...mapState(["smsCountRemain", "fullname", "userId"]),
    realAvatarLink() {
      return (
        this.profileUser?.avatar?.link ||
        this.userInfo?.init?.user?.avatar?.link ||
        null
      );
    },
    hasRealAvatar() {
      return !!this.realAvatarLink;
    },
    avatarPlaceholder() {
      return require("@/assets/Avatar.png");
    },
    displayAvatar() {
      return this.realAvatarLink || this.avatarPlaceholder;
    },
    displayUserName() {
      return this.profileUser?.name || this.fullname || "-";
    },
    hasUsableInitials() {
      const name = (this.displayUserName || "").trim();
      return !!name && name !== "-";
    },
    userInitials() {
      const name = (this.displayUserName || "").trim();
      if (!this.hasUsableInitials) return "";
      const parts = name.split(/\s+/).filter(Boolean);
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).slice(0, 2);
      }
      return name.slice(0, 2);
    },
    displayUserRole() {
      return this.profileUser?.role?.name || "-";
    },
    displayMobile() {
      return this.profileUser?.mobile || "";
    },
    displayEmail() {
      return this.profileUser?.email || "";
    },
    displayUsername() {
      return this.profileUser?.username || "";
    },
    businessLogo() {
      return (
        this.userInfo?.init?.logo?.link || require("@/assets/logo-abr-iron.png")
      );
    },
  },
  watch: {
    menuOpen(val) {
      if (val) {
        this.getServiceInfo();
        this.fetchUserCurrentPlan();
        this.fetchProfileUser();
      }
    },
    pageBusiness(val) {
      if (!val) this.showAccountsList = false;
    },
  },
  mounted() {
    this.userInfo = JSON.parse(localStorage.getItem("token"));
    this.getServiceInfo();
    this.fetchUserCurrentPlan();
    this.fetchProfileUser();
    if (this.$route.query.token) {
      this.tokenUserApp = this.$route.query.token;
    }
  },
  methods: {
    openSheet() {
      this.menuOpen = true;
    },
    fetchProfileUser() {
      if (!this.userId && !this.$store.state.userId) return;
      this.$axios
        .get(this.$endpoint + "getdetail", {
          params: {
            select: "getProfile",
            id: this.userId || this.$store.state.userId,
          },
        })
        .then((res) => {
          this.profileUser = res.data;
        })
        .catch(() => {
          /* keep fallbacks */
        });
    },
    closeMenu() {
      this.menuOpen = false;
    },
    openPage(page) {
      this.closeMenu();
      this.showAccountsList = false;
      this.$nextTick(() => {
        if (page === "business") this.pageBusiness = true;
        if (page === "settings") this.pageSettings = true;
      });
    },
    openChangeAccount() {
      this.closeMenu();
      this.$nextTick(() => {
        this.pageBusiness = true;
        this.openAccountsList();
      });
    },
    closePage() {
      if (this.pageBusiness && this.showAccountsList) {
        this.showAccountsList = false;
        this.pageBusiness = false;
        this.menuOpen = true;
        return;
      }
      this.pageBusiness = false;
      this.pageSettings = false;
      this.menuOpen = true;
    },
    onSheetHandleTouchStart(e) {
      const touch = e.touches && e.touches[0];
      this.sheetHandleTouchY = touch ? touch.clientY : null;
    },
    onSheetHandleTouchEnd(e) {
      if (this.sheetHandleTouchY == null) return;
      const touch = e.changedTouches && e.changedTouches[0];
      const startY = this.sheetHandleTouchY;
      this.sheetHandleTouchY = null;
      if (!touch) return;
      if (touch.clientY - startY < 70) return;
      this.closeMenu();
    },
    getServiceInfo() {
      this.$axios
        .get(this.$endpoint + "getdetail", {
          params: {
            select: "getServiceInfo",
            search: null,
            page: 1,
          },
        })
        .then((r) => {
          this.serviceInfo = r.data;
          this.$store.commit("SET_SMSCOUNTREMAIN", r.data.smsCountRemain);
          this.$store.commit("SET_EXPIREDATE", r.data.expireDate);
        })
        .catch((err) => {
          this.serverErrorHandeler(err);
        });
    },
    openProfileDialog() {
      const app = this;
      this.closeMenu();
      this.profileDialog = true;
      setTimeout(function () {
        if (app.$refs.profileRef) app.$refs.profileRef.getProfile();
      }, 0);
    },
    openTemplateSetting() {
      this.templateSettingDialog = true;
    },
    openUpdates() {
      window.open(
        "https://abriron.com/%d8%a8%d8%b1%d9%88%d8%b2%d8%b1%d8%b3%d8%a7%d9%86%db%8c-%d9%87%d8%a7/",
        "_blank"
      );
    },
    logout() {
      const app = this;
      app.loadingLogOut = true;

      const clearAndRedirect = () => {
        const keep = ["scaleConfig", "selectedPrinter", "posDevice"];
        Object.keys(localStorage).forEach((key) => {
          if (!keep.includes(key)) localStorage.removeItem(key);
        });
        delete app.$axios.defaults.headers.common.Authorization;
        delete app.$axios.defaults.headers.common.domain;
        app.$router.push({ name: "logout" });
      };

      this.$axios
        .post(this.$apipoint + "v2/logout")
        .catch(() => {})
        .finally(() => {
          clearAndRedirect();
          app.loadingLogOut = false;
        });
    },
    fetchUserCurrentPlan() {
      this.$axios
        .get(this.$endpoint + "get-user-plan")
        .then((r) => {
          this.userCurrentPlan = r.data;
          this.$store.commit("SET_USER_CURRENT_PLAN", this.userCurrentPlan);
          if (this.userCurrentPlan) {
            localStorage.setItem("hasTaxPayer", this.userCurrentPlan.has_taxpayer);
            this.setDobarePermission();
          }
        })
        .catch((err) => {
          this.userCurrentPlan = err.response?.data?.data;
          this.$store.commit("SET_USER_CURRENT_PLAN", this.userCurrentPlan);
          this.serverErrorHandeler(err);
        });
    },
    setDobarePermission() {
      if (this.$store.state.userCurrentPlan != null) {
        const dobare = this.$store.state.userCurrentPlan.plan.options.find(
          (item) => item.key == "dobareSystem"
        );
        if (dobare && dobare.value == "active") {
          localStorage.setItem("hasDobarePermission", true);
        } else {
          localStorage.setItem("hasDobarePermission", false);
        }
      }
    },
    goToPlans() {
      this.pageBusiness = false;
      this.$router.push({ name: "plans" });
    },
    chargingPanelSMS() {
      this.pageBusiness = false;
      this.$router.push({
        name: "PlansCheckout",
        query: {
          chargingSMS: true,
          token: this.tokenUserApp ? this.tokenUserApp : null,
        },
      });
    },
    goCreateBusiness() {
      this.pageBusiness = false;
      this.$router.push("/business-Info");
    },
    openAccountsList() {
      const app = this;
      app.loadingAccounts = true;
      app.$axios
        .post(app.$apipoint + "v2/get-user-accounts")
        .then((r) => {
          this.userAccounts = r.data.accounts;
          if (this.userAccounts && this.userAccounts.length > 0) {
            this.showAccountsList = true;
          }
        })
        .catch((err) => {
          app.serverErrorHandeler(err);
        })
        .finally(() => {
          app.loadingAccounts = false;
        });
    },
    findBusiness(val) {
      const app = this;
      app.loadingAccounts = true;
      this.$axios
        .post(this.$apipoint + "v2/switch-account", {
          id: val.id,
        })
        .then((response) => {
          app.loadingAccounts = false;
          localStorage.setItem("token", JSON.stringify(response.data.meta));
          localStorage.setItem("fullname", response.data.username);
          app.$axios.defaults.headers.common.Authorization =
            "Bearer " + response.data.token;
          localStorage.setItem("APP_KEY", val.app_key);

          delete app.$axios.defaults.headers.common.x_api_key;
          delete app.$axios.defaults.headers.common.domain;

          app.$store.commit("SET_FULLNAME", response.data.username);
          app.$store.commit(
            "SET_CURRENCY_SYMBOL",
            response.data.meta.init.currencySymbol
          );
          app.$store.commit("SET_FINANCIALYEAR", response.data.meta.init.financialYear);
          app.$store.commit(
            "SET_BACKGROUNDIMAGE",
            response.data.meta.init.backgroundImage
          );
          app.$store.commit(
            "SET_BACKGROUNDCOLOR",
            response.data.meta.init.backgroundColor
          );
          app.$store.commit("SET_START_DATE", response.data.meta.init.startDate);
          app.$store.commit("SET_END_DATE", response.data.meta.init.endDate);
          app.$store.commit("SET_THEME_COLOR", response.data.meta.init.theme_color.value);
          app.$store.commit("SET_DARK", response.data.meta.init.dark.value);
          app.$store.commit("SET_HOVERMENU", response.data.meta.init.hoverMenu.value);
          app.$store.commit(
            "SET_APPBARSOPACITY",
            response.data.meta.init.appBarsOpacity.value
          );
          app.$store.commit("SET_ACCESS", response.data.meta.init.access);
          app.$store.commit("SET_LANG", response.data.meta.init.lang.value);
          app.$store.commit("SET_RTL", response.data.meta.init.rtl.value);
          app.$store.commit("SET_DATE_LOCALE", response.data.meta.init.date_locale.value);
          app.$store.commit(
            "SET_NEGATIVE_INVENTORY",
            response.data.meta.init.negative_inventory.value
          );
          app.$store.commit("SET_CURRENCY", response.data.meta.init.currency);
          app.$store.commit("SET_MENU_BOOKMARK", response.data.meta.init.menuBookmark);
          app.$store.commit("SET_PERMISSIONS", response.data.meta.init.permissions);
          if (response.data.meta.init.teacherAccess) {
            app.$store.commit("SET_TEACHERACCESS", "active");
            app.$router.push({ name: "academyDashboard" });
          }
          localStorage.setItem("apn", null);
          if (val.plan_days_left == 0 || val.plan_days_left < 0) {
            this.$router.push({ name: "plans" });
          } else {
            app.$router.push("/");
          }

          this.userInfo = JSON.parse(localStorage.getItem("token"));
          this.pageBusiness = false;
          this.showAccountsList = false;
        })
        .catch((err) => {
          app.loadingAccounts = false;
          app.serverErrorHandeler(err);
        })
        .finally(() => {
          app.loadingAccounts = false;
        });
    },
  },
};
</script>

<style scoped>
.user-menu-mobile {
  display: flex;
  align-items: center;
}

.user-menu-mobile__avatar-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.user-menu-mobile__avatar-btn:active .user-menu-mobile__avatar-chip {
  transform: scale(0.94);
  box-shadow: 0 0 0 3px rgba(91, 124, 153, 0.22);
}

.user-menu-mobile__avatar-chip {
  position: relative;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  overflow: hidden;
  background: linear-gradient(145deg, #5b7c99 0%, #3d5166 100%);
  border: 2px solid #fff;
  box-shadow: 0 1px 4px rgba(28, 42, 58, 0.16);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  flex-shrink: 0;
  box-sizing: border-box;
}

.user-menu-mobile__avatar-chip-img {
  width: 100%;
  height: 100%;
  border-radius: 8px;
  object-fit: cover;
  display: block;
}

.user-menu-mobile__avatar-chip-initials {
  font-size: 11px;
  font-weight: 700;
  color: #fff;
  line-height: 1;
  letter-spacing: 0.02em;
}

.user-menu-mobile__sheet {
  border-radius: 16px 16px 0 0 !important;
  padding: 0 0 16px;
  max-height: 88vh;
  overflow-y: auto;
  background: #fff;
}

.user-menu-mobile__handle {
  position: relative;
  width: 40px;
  height: 4px;
  margin: 10px auto 8px;
  border-radius: 999px;
  background: #cfd8dc;
  cursor: grab;
  -webkit-tap-highlight-color: transparent;
  touch-action: none;
}

.user-menu-mobile__handle::before {
  content: "";
  position: absolute;
  top: -14px;
  bottom: -14px;
  left: -40px;
  right: -40px;
}

.user-menu-mobile__header,
.user-menu-mobile__page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 12px 12px;
  border-bottom: 1px solid #eef2f6;
}

.user-menu-mobile__page-header {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #fff;
  padding-top: 12px;
}

.user-menu-mobile__header-title {
  font-size: 16px;
  font-weight: 700;
  color: #263238;
  text-align: center;
}

.user-menu-mobile__header-spacer,
.user-menu-mobile__icon-btn {
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.user-menu-mobile__icon-btn {
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
}

.user-menu-mobile__body,
.user-menu-mobile__page-body {
  padding: 12px 12px 8px;
}

.user-menu-mobile__user {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 16px 12px 14px;
  margin-bottom: 10px;
  text-align: center;
  border-radius: 16px;
  background: linear-gradient(180deg, #f7f9fc 0%, #eef3f8 100%);
  border: 1px solid #e1e7ef;
}

.user-menu-mobile__user-photo-wrap {
  position: relative;
  width: 76px;
  height: 76px;
}

.user-menu-mobile__user-avatar {
  width: 76px;
  height: 76px;
  border-radius: 16px;
  object-fit: cover;
  border: 2px solid #fff;
  box-shadow: 0 2px 10px rgba(45, 70, 100, 0.12);
  display: block;
  background: #dce4ee;
}

.user-menu-mobile__user-edit {
  position: absolute;
  left: -4px;
  bottom: -4px;
  width: 28px;
  height: 28px;
  border: 2px solid #fff;
  border-radius: 50%;
  background: #5b7c99;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(45, 70, 100, 0.2);
  -webkit-tap-highlight-color: transparent;
}

.user-menu-mobile__user-texts {
  width: 100%;
  text-align: center;
}

.user-menu-mobile__user-name {
  font-size: 17px;
  font-weight: 700;
  color: #1c2a3a;
  line-height: 1.35;
  letter-spacing: -0.01em;
}

.user-menu-mobile__user-role {
  display: inline-block;
  margin-top: 6px;
  padding: 3px 10px;
  font-size: 11px;
  font-weight: 600;
  color: #4a657a;
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid #d7e0ea;
  border-radius: 999px;
}

.user-menu-mobile__user-summary {
  width: 100%;
  margin-top: 4px;
  padding: 8px 10px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.72);
  border: 1px solid #e1e7ef;
}

.user-menu-mobile__user-summary-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 5px 2px;
  font-size: 11px;
  color: #6b7f93;
}

.user-menu-mobile__user-summary-row strong {
  font-size: 12px;
  font-weight: 600;
  color: #1c2a3a;
  direction: ltr;
  text-align: left;
}

.user-menu-mobile__logout-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 100%;
  min-height: 42px;
  margin-top: 2px;
  border: 1px solid #ffcdd2;
  border-radius: 12px;
  background: #fff5f5;
  color: #e53935;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.user-menu-mobile__logout-btn:active {
  background: #ffebee;
}

.user-menu-mobile__page {
  display: flex;
  flex-direction: column;
  background: #f7f9fc;
  min-height: 100%;
  height: 100%;
}

.user-menu-mobile__page--accounts {
  height: 100vh;
  max-height: 100vh;
}

.user-menu-mobile__page-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  background: #f7f9fc;
  -webkit-overflow-scrolling: touch;
}

.user-menu-mobile__page-footer {
  flex: 0 0 auto;
  flex-shrink: 0;
  z-index: 3;
  margin-top: auto;
  padding: 10px 16px calc(20px + env(safe-area-inset-bottom, 0px));
  background: #fff;
  border-top: 1px solid #eeeeee;
}

.user-menu-mobile__create-business-btn {
  margin: 0 !important;
  border-radius: 10px !important;
  font-weight: 700;
}

/* title report */
.user-menu-mobile__report {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px;
  margin-bottom: 12px;
  border-radius: 14px;
  background: #f4f7fb;
  border: 1px solid #d9e2ec;
  box-shadow: inset -3px 0 0 #7a93ad;
}

.user-menu-mobile__report-action {
  margin-top: 4px;
  align-self: flex-start;
}

.user-menu-mobile__report--page {
  background: #fff;
  box-shadow: inset -3px 0 0 #7a93ad;
}

.user-menu-mobile__report-logo {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  object-fit: cover;
  border: 1px solid #cfd8e3;
  background: #3d5166;
  flex-shrink: 0;
  box-shadow: 0 1px 3px rgba(28, 42, 58, 0.08);
}

.user-menu-mobile__report-texts {
  flex: 1;
  min-width: 0;
  text-align: right;
}

.user-menu-mobile__report-name {
  font-size: 14px;
  font-weight: 700;
  color: #1c2a3a;
  line-height: 1.4;
}

.user-menu-mobile__report-key {
  font-size: 12px;
  color: #6b7f93;
  margin-top: 3px;
  direction: ltr;
  text-align: right;
  font-weight: 500;
}

.user-menu-mobile__report-line {
  font-size: 11px;
  color: #5a6f82;
  margin-top: 5px;
  line-height: 1.55;
}

.user-menu-mobile__dot {
  margin: 0 4px;
  color: #9aabbc;
}

/* menu rows */
.user-menu-mobile__row {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 52px;
  padding: 12px 14px;
  margin-top: 8px;
  border: 1px solid #e6ebf1;
  border-radius: 12px;
  background: #f5f7fa;
  text-align: right;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.user-menu-mobile__page-body .user-menu-mobile__row {
  background: #fff;
}

.user-menu-mobile__row:active {
  background: #e8eef4;
}

.user-menu-mobile__row--danger {
  background: #fff5f5;
  border-color: #ffcdd2;
}

.user-menu-mobile__row--danger .user-menu-mobile__row-label {
  color: #e53935;
  font-weight: 600;
}

.user-menu-mobile__row-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #e8eef4;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
}

.user-menu-mobile__row-icon--danger {
  background: #ffebee;
}

.user-menu-mobile__row-avatar {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.user-menu-mobile__row-texts {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  text-align: right;
}

.user-menu-mobile__row-label {
  flex: 1;
  font-size: 13px;
  color: #37474f;
  font-weight: 600;
}

.user-menu-mobile__row-action {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 8px;
  background: #e8eef4;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.user-menu-mobile__row-action:disabled {
  opacity: 0.7;
  cursor: default;
}

.user-menu-mobile__row-sub {
  font-size: 11px;
  color: #90a4ae;
}

.user-menu-mobile__profile-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px;
  margin-bottom: 8px;
  border-radius: 12px;
  background: #fff;
  border: 1px solid #e6ebf1;
}

.user-menu-mobile__profile-avatar {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #e3e8ef;
}

.user-menu-mobile__account-row {
  margin-top: 8px;
}

.user-menu-mobile__days {
  font-size: 11px;
  color: #78909c;
  white-space: nowrap;
}
</style>
